import { useTranslation } from '@kgc/i18n';

import type { ProcurementRequest } from '@/api/procurement';
import { Button } from '@/components/ui';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Procurement request statuses that count as "still in procurement" (pending)
 * when deciding whether to warn before closing a worksheet. RECEIVED (arrived)
 * and CANCELLED are NOT pending.
 */
export const PENDING_PROCUREMENT_STATUSES = ['DRAFT', 'SUBMITTED', 'ORDERED'] as const;

export function isPendingProcurement(req: Pick<ProcurementRequest, 'status'>): boolean {
  return (PENDING_PROCUREMENT_STATUSES as readonly string[]).includes(req.status);
}

interface CloseWorksheetConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  pendingRequests: ProcurementRequest[];
  isClosing?: boolean;
}

/**
 * Q-126-01: non-blocking confirm-warning shown when closing a worksheet that
 * still has pending procurement requests. The manager can proceed anyway
 * ("Mégis lezárom"). Reversible by design — a later hard-block is one flag away.
 */
export function CloseWorksheetConfirmDialog({
  open,
  onClose,
  onConfirm,
  pendingRequests,
  isClosing = false,
}: CloseWorksheetConfirmDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent data-testid="close-worksheet-confirm">
        <DialogHeader>
          <DialogTitle>{t('worksheet.closeConfirm.title')}</DialogTitle>
          <DialogDescription>
            {t('worksheet.closeConfirm.warning', { count: pendingRequests.length })}
          </DialogDescription>
        </DialogHeader>

        <ul
          data-testid="close-confirm-pending-list"
          className="my-2 max-h-60 space-y-2 overflow-y-auto"
        >
          {pendingRequests.map(req => (
            <li
              key={req.id}
              data-testid={`close-confirm-item-${req.id}`}
              className="flex items-center justify-between gap-2 rounded-md border border-gray-200 px-3 py-2 text-sm dark:border-[#2d7d8a]"
            >
              <span className="min-w-0 flex-1 truncate text-gray-900 dark:text-white">
                {req.notes?.trim() || t('worksheet.closeConfirm.unnamedItem')}
              </span>
              <span className="shrink-0 text-gray-500 dark:text-[#ccfbf1]">
                {t('worksheet.closeConfirm.qty', { qty: req.quantity })}
              </span>
              <span className="shrink-0 rounded-full bg-orange-100 px-2 py-0.5 text-xs font-medium text-orange-700 dark:bg-orange-900/50 dark:text-orange-300">
                {t(`worksheet.closeConfirm.status.${req.status}`) || req.status}
              </span>
            </li>
          ))}
        </ul>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isClosing}>
            {t('worksheet.closeConfirm.cancel')}
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isClosing}
            data-testid="close-confirm-proceed"
            className="border-orange-500 bg-orange-600 text-white hover:bg-orange-700"
          >
            {t('worksheet.closeConfirm.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
